import React, { useContext, useEffect, useState } from "react";
import classes from "./EditServices.module.scss";
import { Collapse, Container, OverlayTrigger, Row, Tooltip,Col } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { DDD, E2Data } from "../lib/ddd/ddd";
import { OrgIdContext } from "../store/orgId_context";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Button from "../components/UI/Button";
import LoadingDots from "../components/UI/LoadingDots";
import { useForm, Controller } from "react-hook-form";
import WarningTooltip from "../components/profileComponents/WarningTooltip";
import FileUploader from "../components/profileComponents/fields/FileUploader";
import ImageUploader from "../components/profileComponents/fields/ImageUploader";
import { NotificationManager} from 'react-notifications';
import HelpIcon from '@mui/icons-material/Help';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { resetOwnPassword } from "../lib/resetOwnPassword";
import { isOAuthAccount, isOwnUser } from "../lib/accountSource";

const apiRequestAddress = process.env.REACT_APP_API_ENDPOINT || window.location.origin + "/json.rpc";

const e2Data = new E2Data("EU_AOrg");
e2Data.format="Full";
const ddd = new DDD(apiRequestAddress);
const e2DataCountries = new E2Data("ConCountries");
e2DataCountries.format="Full";
e2DataCountries.take = 1000;
e2DataCountries.orderBy = "CountryNameOriginal ASC";

const e2DataCultures = new E2Data("ConCountriesCultureInfoCode");
e2DataCultures.format="Full";
e2DataCultures.take = 1000;
e2DataCultures.orderBy = "CountryCodeISOC3__CountryNameOriginal ASC";
/*hey. na DDDIDEV imaš zdaj build, ki ima naslednje 2 novosti:

Auth.Describe in Auth.GetSessionInfo zdaj vrneta tudi sekcijo "Profile", ki ima nekaj atributov, ki pridejo direktno iz SysUsers (en od njih v resnici ne: FullName - ta je tudi readonly).
nov API Auth.UpdateProfile sprejme JSON objekt, v katerem lahko user za sebe spremeni naslednje Profile atribute:

Name
LastName
Email
Culture
Country
MobilePhone*/

const UserSettings = () => {
    const { aboutOrgId, sessionInfo, setSessionInfoHandler} = useContext(OrgIdContext);
    const [invoiceId, setInvoiceId] = useState();
    const [noInvoiceFound, setNoInvoiceFound] = useState(false);
    const [creatingNewInvoice, setCreatingNewInvoice] = useState(false);
    const [creatingError, setCreatingError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [forceRefresh, setForceRefresh] = useState(0);
    const {t} = useTranslation();
    
    const {isPending : isDataPending, data:culturesList} = useQuery({
        queryKey: ['Data.GetEntityData', e2DataCultures.GetParamsObject()],
        queryFn: () => e2DataCultures.GetData(),
        refetchOnWindowFocus: false,
    })

    const {isPending : isDataCountiesPending, data:countriesList} = useQuery({
        queryKey: ['Data.GetEntityData', e2DataCountries.GetParamsObject()],
        queryFn: () => e2DataCountries.GetData(),
        refetchOnWindowFocus: false,
    })

    // This user's own row from the team list — the only place IsOAuth is available.
    // `checked` stays false until the call comes back, so the key isn't shown and then
    // pulled away again for a Google account.
    const [ownTeamRow, setOwnTeamRow] = useState(null);
    const [ownTeamRowChecked, setOwnTeamRowChecked] = useState(false);

    const {
        register,
        reset,
        formState: { errors },
        handleSubmit,
        control,
        setValue,
    } = useForm(/*defaultone*/);

    useEffect(() => {
        loadDataFromSessionInfo();
    }, [sessionInfo, reset]);

    // Whether this account signs in through Google is only told by EUeInvoices
    // .DDDI_GetMyUsers, which returns IsOAuth per team member — Auth.Describe, which feeds
    // everything else on this page, carries no such field. So the team list is fetched here
    // purely to find this user's own row and read that one boolean, the same source the
    // team page uses for its key icon.
    useEffect(() => {
        if(!sessionInfo?.User) return;
        ddd.Rpc_ExecuteExtension("EUeInvoices.DDDI_GetMyUsers").then(res => {
            const rows = res?.result?.Result;
            const own = Array.isArray(rows) ? rows.find(u => isOwnUser(u, sessionInfo.User)) : null;
            setOwnTeamRow(own || null);
            setOwnTeamRowChecked(true);
        }).catch(() => setOwnTeamRowChecked(true));
    }, [sessionInfo?.User?.Id, sessionInfo?.User?.PrimaryUsername]);

    // Prefers the team row's IsOAuth; falls back to the session user if the row can't be
    // found (a user without rights to list the team, say), where no provider field exists
    // and the answer is "not OAuth" — so a password user keeps their reset option.
    const isOAuthUser = isOAuthAccount(ownTeamRow || sessionInfo?.User);


    const loadDataFromSessionInfo = () =>{
        if (!sessionInfo?.Profile) return;
        reset({
            Name: sessionInfo.Profile.Name || "",
            LastName: sessionInfo.Profile.LastName || "",
            Email: sessionInfo.Profile.Email || "",
            MobilePhone: sessionInfo.Profile.MobilePhone || "",
            Culture: sessionInfo.Profile.Culture || "",
            Country: sessionInfo.Profile.Country || "",
        });
    }

    const saveDataSubmit = (data) =>{
        const ddd = new DDD(apiRequestAddress);
        ddd.Rpc_ExecuteMethod("Auth.UpdateProfile", {profile : data}).then((res) =>{
            if(res?.result ==="ok"){
                NotificationManager.success("", t("Saved!"));
                setTimeout(() => refetchSessionData(), 0);
            }else{
                NotificationManager.warning("", res?.error?.data || t("Unable to save data!"));
            }
        });
    }

    const refetchSessionData = () => {
        ddd.InvalidateSessionExtensions().then(() => {
            ddd.GetSessionAuthDescribe({includeExtendedInfo: true}).then(res => {
                if(res?.result)
                    setSessionInfoHandler(res.result);
            });
        });
        /*ddd.GetSessionAuthDescribe({includeExtendedInfo: true}).then(res => {
                if(res?.result)
                    setSessionInfoHandler(res.result);
        });*/
    }

    return (
    <>
      <Helmet>
        <title>User settings | DDD Invoices</title>
      </Helmet>
      <div className={`h-100 w-100 d-flex justify-content-center align-items-start p-5 ${classes.container}`}>
        <div className="w-100" style={{ maxWidth: "780px" }}>

            {/* Header */}
            <div className="mb-4">
                <h2 className="mb-1">{t("User settings")}</h2>
                <div className="text-muted">{t("Update your personal information and localization settings.")}</div>
            </div>

            {/* Card */}
            <div
                className="p-4"
                style={{
                    boxShadow: "var(--box-shadow-card)",
                    borderRadius: "var(--border-radius-card)"
                }}
            >
                <form
                    id="invoiceForm"
                    className={classes["settings-form"]}
                    onSubmit={handleSubmit(saveDataSubmit)}
                >

                    {/* Section: User Information */}
                    <div className="mb-3 pb-2" style={{ borderBottom: "1px solid #ddd" }}>
                        <h5 className="m-0">{t("User Information")}</h5>
                    </div>

                    {/* GRID */}
                    <div
                        className="row g-4"
                    >
                        {/* Name */}
                        <div className="col-md-6">
                            <fieldset className={classes["input-container"]}>
                                <label htmlFor="Name">
                                    {t("Name")}*
                                    {errors.Name && <WarningTooltip text={errors.Name.message} />}
                                </label>
                                <input
                                    id="Name"
                                    type="text"
                                    {...register("Name", { required: t("Name is required") })}
                                    style={errors.Name ? { outlineColor: "red" } : {}}
                                    placeholder={t("Enter your name")}
                                />
                            </fieldset>
                        </div>

                        {/* LastName */}
                        <div className="col-md-6">
                            <fieldset className={classes["input-container"]}>
                                <label htmlFor="LastName">
                                    {t("Last name")}*
                                    {errors.LastName && <WarningTooltip text={errors.LastName.message} />}
                                </label>
                                <input
                                    id="LastName"
                                    type="text"
                                    {...register("LastName", { required: t("Last name is required") })}
                                    style={errors.LastName ? { outlineColor: "red" } : {}}
                                    placeholder={t("Enter your last name")}
                                />
                            </fieldset>
                        </div>

                        {/* Username — display only. Deliberately not register()ed: Save sends
                            every registered field to Auth.UpdateProfile, and this is the login
                            name, not profile data. Read from the session (Auth.Describe),
                            which is the same server data the rest of this form comes from. */}
                        <div className="col-md-6">
                            <fieldset className={classes["input-container"]}>
                                <label htmlFor="Username">
                                    {t("Username")}
                                    <OverlayTrigger placement="top" overlay={<Tooltip>{t("The username you log in with. It can't be changed here.")}</Tooltip>}>
                                        <HelpOutlineIcon style={{marginLeft:"0.3rem", fontSize:"1rem"}}/>
                                    </OverlayTrigger>
                                    {/* Same key as on the team page (UsersTable), same action.
                                        Prefilled with the registration address from the
                                        session — not the Email field below, which is contact
                                        only and wouldn't find the account in the reset funnel.
                                        Hidden for Google accounts: they have no password, so
                                        the funnel has nothing to reset. */}
                                    {ownTeamRowChecked && !isOAuthUser &&
                                        <OverlayTrigger placement="top" overlay={<Tooltip>{t("Reset your password")}</Tooltip>}>
                                            <VpnKeyIcon
                                                style={{marginLeft:"0.3rem", fontSize:"1.1rem", color:"var(--main-blue)", cursor:"pointer"}}
                                                onClick={() => resetOwnPassword(sessionInfo?.User?.Email)}
                                            />
                                        </OverlayTrigger>
                                    }
                                </label>
                                <input
                                    id="Username"
                                    type="text"
                                    readOnly
                                    value={sessionInfo?.User?.PrimaryUsername || ""}
                                    placeholder={t("Username")}
                                    style={{ backgroundColor: "#F5F6F8", color: "#5A6B7B", cursor: "default" }}
                                />
                                {ownTeamRowChecked && isOAuthUser &&
                                    <div className="text-muted" style={{fontSize: "0.85rem", marginTop: "0.35rem"}}>
                                        {t("You sign in with Google, so this account has no password to reset.")}
                                    </div>
                                }
                            </fieldset>
                        </div>

                        {/* Email */}
                        <div className="col-md-6">
                            <fieldset className={classes["input-container"]}>
                                <label htmlFor="Email">
                                    {t("Email (contact only)")}*
                                    <OverlayTrigger placement="top" overlay={<Tooltip>{t("This is only contact email, not your registration email. Changing this won’t affect your registration. If you’d like to change your registration email, contact us.")}</Tooltip>}>
                                        <HelpOutlineIcon style={{marginLeft:"0.3rem", fontSize:"1rem"}}/>
                                    </OverlayTrigger>
                                    {errors.Email && <WarningTooltip text={errors.Email.message} />}
                                </label>
                                <input
                                    id="Email"
                                    type="text"
                                    {...register("Email", { required: t("Email is required") })}
                                    style={errors.Email ? { outlineColor: "red" } : {}}
                                    placeholder={t("example@email.com")}
                                />
                            </fieldset>
                        </div>

                        {/* Mobile phone */}
                        <div className="col-md-6">
                            <fieldset className={classes["input-container"]}>
                                <label htmlFor="MobilePhone">
                                    {t("Mobile phone")}
                                    <OverlayTrigger placement="top" overlay={<Tooltip>{t("Enter mobile phone, if you’d like for us to contact you faster in the case that would be required.")}</Tooltip>}>
                                        <HelpOutlineIcon style={{marginLeft:"0.3rem", fontSize:"1rem"}}/>
                                    </OverlayTrigger>
                                    {errors.MobilePhone && <WarningTooltip text={errors.MobilePhone.message} />}
                                </label>
                                <input
                                    id="MobilePhone"
                                    type="text"
                                    {...register("MobilePhone")}
                                    style={errors.MobilePhone ? { outlineColor: "red" } : {}}
                                    placeholder={t("Telephone number")}
                                />
                            </fieldset>
                        </div>

                        {/* Country */}
                        <div className="col-md-6">
                            <fieldset className={classes["input-container"]}>
                                <label htmlFor="Country">{t("Country")}
                                    <OverlayTrigger placement="top" overlay={<Tooltip>{t("Your place of work, country where you are sitting in.")}</Tooltip>}>
                                        <HelpOutlineIcon style={{marginLeft:"0.3rem", fontSize:"1rem"}}/>
                                    </OverlayTrigger>
                                </label>
                                
                                <Controller
                                    name="Country"
                                    control={control}
                                    render={({ field }) => (
                                        <select
                                            {...field}
                                            onChange={(e) => {
                                                field.onChange(e);
                                            }}
                                        >
                                            {countriesList?.result?.data?.map((country) => (
                                                <option
                                                    key={country.CountryNameOriginal}
                                                    value={country.CountryNameOriginal}
                                                >
                                                    {country.CountryNameOriginal} ({country.CountryCodeISOC2})
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                />
                                <p className={classes.error}>{errors.Country?.message}</p>
                            </fieldset>
                        </div>

                        {/* Culture */}
                        <div className="col-md-6">
                            <fieldset className={classes["input-container"]}>
                                <label htmlFor="Culture">{t("Culture")}
                                   < OverlayTrigger placement="top" overlay={<Tooltip>{t("Changes the language & culture of the UI for you.")}</Tooltip>}>
                                        <HelpOutlineIcon style={{marginLeft:"0.3rem", fontSize:"1rem"}}/>
                                </OverlayTrigger>
                                </label>
                                <Controller
                                    name="Culture"
                                    control={control}
                                    render={({ field }) => (
                                        <select {...field}>
                                            {culturesList?.result?.data?.map((culture) => (
                                                <option key={culture.CultureInfoCode} value={culture.CultureInfoCode}>
                                                    {culture.CountryCodeISOC3__CountryNameOriginal} ({culture.CultureInfoCode})
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                />
                            </fieldset>
                        </div>
                    </div>

                    {/* Save button fixed to bottom of card */}
                    <div className="d-flex justify-content-end mt-4">
                        <Button
                            type="submit"
                            className="px-5 py-2"
                            disabled={saving}
                            /* onClick={() =>
                                document
                                    .getElementById("invoiceForm")
                                    .dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }))
                            } */
                        >
                            {t("Save")}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    </div>

      {/* <div className={`h-100 w-100 p-5 ${classes.container}`}>
            <div className="">
                <h2>{t("User settings")}</h2>
            </div> 
            <div className="flex-fill ps-2 pe-2" style={{overflow:"auto"}}>
                <form id="invoiceForm" className={classes["settings-form"]} onSubmit={handleSubmit(saveDataSubmit)}>
                    <div className="mt-3" style={{borderBottom:"1px solid", fontWeight:"bold"}}>{t("Company")}:</div>
                    <fieldset className={classes["input-container"]}>
                        <label htmlFor="Web">{t("Website URL")}
                        {errors.Web && <>
                            <WarningTooltip text={errors.Web?.message}/>
                            </>}</label>
                        <input
                            type="text"
                            name="Web"
                            id="Web"
                            style={errors.Web ? {outlineColor:"red"}: {}}
                            {...register("Web")}
                            placeholder={t("Enter your website URL")}
                        />
                    </fieldset>
                    <fieldset className={classes["input-container"]}>
                        <label htmlFor="Country">{t("Country")}*</label>
                        <Controller
                            // type="text"
                            name="Country"
                            id="Country"
                            control={control}
                            render={({ field }) => {
                            return (
                                <select {...field} onChange={(e) =>{
                                onCountryChange(e);
                                    field.onChange(e);
                                }}>
                                {countriesList?.result?.data?.map((country) => {
                                    return (
                                    <option
                                        value={country.CountryCodeISOC2}
                                        key={country.CountryCodeISOC2}
                                    >
                                        {country.CountryNameOriginal} ({country.CountryCodeISOC2})
                                    </option>
                                    );
                                })}
                                </select>
                            );
                            }}
                        />
                        <p className={classes.error}>{errors.CountryCode?.message}</p>
                    </fieldset>
                    <fieldset className={classes["input-container"]}>
                        <label htmlFor="Email">{t("Email")}
                        {errors.Email && <>
                            <WarningTooltip text={errors.Email?.message}/>
                            </>}</label>
                        <input
                            type="text"
                            name="Email"
                            id="Email"
                            style={errors.Email ? {outlineColor:"red"}: {}}
                            {...register("Email")}
                            placeholder={t("example@email.com")}
                        />
                    </fieldset>
                    <fieldset className={classes["input-container"]}>
                        <label htmlFor="Telephone">{t("Telephone")}
                        {errors.Telephone && <>
                            <WarningTooltip text={errors.Telephone?.message}/>
                            </>}</label>
                        <input
                            type="text"
                            name="Telephone"
                            id="Telephone"
                            style={errors.Telephone ? {outlineColor:"red"}: {}}
                            {...register("Telephone")}
                            placeholder={t("Telephone number")}
                        />
                    </fieldset>

                    <div className="d-flex justify-content-around">
                        <fieldset className={classes["input-container"]}>
                            <label htmlFor="Logo">{t("Logo")}
                            {errors.Logo && <>
                                <WarningTooltip text={errors.Logo?.message}/>
                                </>}</label>
                            <ImageUploader name={"Logo"}
                                        register={register}
                                        setValue={setValue}
                                        control={control}
                                        inEditMode = {true}/>
                        </fieldset>
                        <fieldset className={classes["input-container"]}>
                        <label htmlFor="Logo">{t("Stamp")}
                            {errors.Stamp && <>
                                <WarningTooltip text={errors.Stamp?.message}/>
                                
                                </>}</label>
                            <ImageUploader name={"Stamp"}
                                        register={register}
                                        setValue={setValue}
                                        control={control}
                                        inEditMode = {true}/>
                        </fieldset>
                    </div>
                    <div className="mt-5" style={{borderBottom:"1px solid", fontWeight:"bold"}}>{t("Signature")}:</div>
                    <div className="d-flex justify-content-around">
                        <fieldset className={classes["input-container"]}>
                            <label htmlFor="Signer">{t("Signer")}
                            {errors.Signer && <>
                                <WarningTooltip text={errors.Signer?.message}/>
                                </>}</label>
                            <input
                                type="text"
                                name="WeSignerb"
                                id="Signer"
                                style={errors.Telephone ? {outlineColor:"red"}: {}}
                                {...register("Signer")}
                                placeholder={t("Enter signer’s name")}
                            />
                        </fieldset>
                        <fieldset className={classes["input-container"]}>
                            <label htmlFor="SignScan">{t("Signiture scan")}
                            {errors.SignScan && <>
                                <WarningTooltip text={errors.SignScan?.message}/>
                                
                                </>}</label>
                            <FileUploader name={"SignScan"}
                                        register={register}
                                        setValue={setValue}
                                        control={control}
                                        inEditMode = {true}/>
                        </fieldset>
                    </div>
                </form>
            </div>
            <div className="text-center mt-4">
                <Button type="submit" className="px-5 py-1 ms-3" disabled ={saving} onClick={() => document.getElementById('invoiceForm').dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))}>
                    {t("Save")}
                </Button>
            </div>
      </div> */}
    </>
    );
};

export default UserSettings;
